// Time specs of automation actions (spec 05 §4 delays, §10.4 business days).
import { addBusinessDays } from "../gavion/time/businessDays.ts";
import type { CallingHours } from "../gavion/time/callingHours.ts";
import { BUSINESS_TZ, localDateOf } from "../gavion/time/torontoTime.ts";
import type { ActionSpec, Delay } from "./types.ts";
import { getPath } from "./templates.ts";

/**
 * The slice of GavionConfig (configuration.config.gavion, merged with defaults by the runner)
 * that the planner reads. Structural, so the full GavionConfig satisfies it.
 */
export type PlannerConfig = {
  max_attempts: number;
  business_days: readonly number[];
  holidays: readonly string[];
  callback_default_time: string;
  calling_hours: CallingHours & { timezone_default: string };
};

/** Business-day arithmetic always counts Toronto calendar days (BUSINESS_TZ). */
export type BusinessCalendar = {
  businessDays: readonly number[];
  holidays: readonly string[];
};

export const calendarOf = (config: PlannerConfig): BusinessCalendar => ({
  businessDays: config.business_days,
  holidays: config.holidays,
});

export const MAX_BUSINESS_DAYS = 366;
export const isValidBusinessDays = (n: unknown): n is number =>
  Number.isInteger(n) &&
  (n as number) >= 0 &&
  (n as number) <= MAX_BUSINESS_DAYS;

export type TimeSkip = "time_source_missing" | "time_in_past" | "invalid_time";
export type TimeResult = { at: Date | null; skip?: TimeSkip };

const HOUR_MS = 3_600_000;
const INVALID: TimeResult = { at: null, skip: "invalid_time" };
const MISSING: TimeResult = { at: null, skip: "time_source_missing" };
const isNum = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const asDate = (v: unknown): Date | null => {
  if (typeof v !== "string") return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t) : null;
};
/**
 * Resolves a delay against the event context. Relative delays count from `anchor` (the event's
 * created_at, so replays are deterministic); any result before `now` is clamped to `now`, except
 * `before`, which is skipped as time_in_past. Never throws: malformed specs are skipped.
 */
export function resolveTime(
  spec: Delay,
  ctx: unknown,
  now: Date,
  cal: BusinessCalendar,
  anchor: Date = now,
): TimeResult {
  if (!spec || typeof spec !== "object") return INVALID;
  if (!Number.isFinite(now.getTime())) return INVALID;
  const from = Number.isFinite(anchor.getTime()) ? anchor : now;
  const valid = (d: Date): TimeResult =>
    !Number.isFinite(d.getTime())
      ? INVALID
      : { at: d.getTime() < now.getTime() ? now : d };
  const s = spec as Record<string, unknown>;
  if ("business_days" in s) {
    if (typeof s.at !== "string" || !isValidBusinessDays(s.business_days))
      return INVALID;
    try {
      const start = localDateOf(from, BUSINESS_TZ);
      return valid(
        addBusinessDays(
          start,
          s.business_days,
          s.at,
          cal.holidays,
          cal.businessDays,
        ),
      );
    } catch {
      return INVALID;
    }
  }
  if ("before" in s) {
    if (typeof s.before !== "string" || !isNum(s.hours)) return INVALID;
    const base = asDate(getPath(ctx, s.before));
    if (!base) return MISSING;
    const at = new Date(base.getTime() - s.hours * HOUR_MS);
    return at.getTime() < now.getTime()
      ? { at: null, skip: "time_in_past" }
      : { at };
  }
  if ("from" in s) {
    if (typeof s.from !== "string") return INVALID;
    const base = asDate(getPath(ctx, s.from));
    return base ? valid(base) : MISSING;
  }
  if ("minutes" in s)
    return isNum(s.minutes)
      ? valid(new Date(from.getTime() + s.minutes * 60_000))
      : INVALID;
  if ("hours" in s)
    return isNum(s.hours)
      ? valid(new Date(from.getTime() + s.hours * HOUR_MS))
      : INVALID;
  return INVALID;
}

/**
 * Time parameter of set_callback (`at` object, `from`, `before`+`hours`, inline delay, or a lone
 * `delay`) and create_task (`due`). Null for actions without one.
 */
export function timeSpecOf(a: ActionSpec, defaultTime = "10:00"): Delay | null {
  if (a.type === "create_task")
    return a.due && typeof a.due === "object" ? (a.due as Delay) : null;
  if (a.type !== "set_callback") return null;
  if (a.at && typeof a.at === "object") return a.at as Delay;
  if (typeof a.from === "string") return { from: a.from };
  if (typeof a.before === "string")
    return { before: a.before, hours: a.hours as number };
  if (a.business_days !== undefined)
    return {
      business_days: a.business_days as number,
      at: typeof a.at === "string" ? a.at : defaultTime,
    };
  if (a.minutes !== undefined) return { minutes: a.minutes as number };
  if (a.hours !== undefined) return { hours: a.hours as number };
  if (a.delay && typeof a.delay === "object") return a.delay;
  return null;
}

/** True when a set_callback takes its callback time from its `delay` (no other time given). */
export const callbackUsesDelay = (a: ActionSpec): boolean =>
  a.type === "set_callback" &&
  !!a.delay &&
  timeSpecOf({ ...a, delay: undefined }) === null;
